// src/pages/BlogPost.js (or wherever you keep your pages)

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async"; //
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../Firebase/firebase";

export default function BlogPost() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  function parseBold(text) {
    // Converts **text** → <strong>text</strong>
    if (typeof text !== "string") return text ?? null;
    const parts = text.split(/(\*\*[^\*]+\*\*)/g);

    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={i} className="font-semibold text-gray-900">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return part;
    });
  }

  function formatBlogText(rawText) {
    if (typeof rawText !== "string" || rawText.length === 0) return null;
    const lines = rawText.split("\n");

    const elements = [];
    let listItems = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ol className="list-decimal pl-6 my-4 space-y-2" key={Math.random()}>
            {listItems.map((item, i) => (
              <li key={i} className="text-gray-800 leading-relaxed">
                {parseBold(item)}
              </li>
            ))}
          </ol>
        );
        listItems = [];
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      // Divider rule: "--"
      if (/^--$/.test(trimmed)) {
        flushList();
        elements.push(
          <hr
            key={`hr-${index}`}
            className="my-8 border-t border-gray-200 w-full"
          />
        );
        return;
      }

      // Numbered list rule
      if (/^\d+\.\s+/.test(trimmed)) {
        const item = trimmed.replace(/^\d+\.\s+/, "");
        listItems.push(item);
        return;
      }

      flushList();

      // Heading rule
      if (trimmed.startsWith("## ")) {
        elements.push(
          <h2
            key={`h2-${index}`}
            className="text-2xl font-bold text-gray-900 mt-10 mb-4"
          >
            {parseBold(trimmed.replace("## ", ""))}
          </h2>
        );
        return;
      }

      // Paragraph
      if (trimmed.length > 0) {
        elements.push(
          <p key={`p-${index}`} className="mb-4 text-gray-800 leading-relaxed">
            {parseBold(trimmed)}
          </p>
        );
      }
    });

    flushList();

    return elements;
  }

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const docRef = doc(db, "blogs", postId);
        const snapshot = await getDoc(docRef);

        if (!snapshot.exists()) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setPost({ id: snapshot.id, ...snapshot.data() });
        setLoading(false);
      } catch (err) {
        console.error("Error fetching blog post:", err);
        setError(err);
        setLoading(false);
      }
    };

    if (postId) {
      fetchPost();
    }
  }, [postId]);

  const canonicalUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/blog/${postId}`
      : "";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <p className="text-gray-500 text-sm tracking-wide uppercase">
          Loading article...
        </p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-3">
          Article not found
        </h1>
        <p className="text-gray-600 mb-6">
          The blog post you’re looking for doesn’t exist or may have been
          removed.
        </p>
        <Link
          to="/blog"
          className="inline-flex items-center px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
        >
          ← Back to Blog
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Something went wrong
        </h1>
        <p className="text-gray-600 mb-4 text-sm">
          We couldn’t load this article. Please try again later.
        </p>
        <p className="text-xs text-gray-400 mb-6">
          {error.code}: {error.message}
        </p>
        <Link
          to="/blog"
          className="inline-flex items-center px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
        >
          ← Back to Blog
        </Link>
      </div>
    );
  }

  if (!post) return null;

  const { title, excerpt, author, date, readTime, category, image, text } =
    post;

  // JSON-LD structured data for SEO
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: excerpt,
    author: {
      "@type": "Person",
      name: author || "Justin Young",
    },
    datePublished: date || "",
    image: image || "",
    mainEntityOfPage: canonicalUrl,
  };

  return (
    <>
      <Helmet>
        <title>{title ? `${title} | Gentle Gourmet Blog` : "Blog Post"}</title>
        {excerpt && <meta name="description" content={excerpt} />}

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        {title && <meta property="og:title" content={title} />}
        {excerpt && <meta property="og:description" content={excerpt} />}
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        {image && <meta property="og:image" content={image} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        {title && <meta name="twitter:title" content={title} />}
        {excerpt && <meta name="twitter:description" content={excerpt} />}
        {image && <meta name="twitter:image" content={image} />}

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(articleJsonLd)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Top nav/back */}
        <div className="border-b border-gray-200 bg-white/80 backdrop-blur">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Link
              to="/blog"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <span className="mr-2">←</span> Back to Blog
            </Link>
            {category && (
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
                {category}
              </span>
            )}
          </div>
        </div>

        {/* Hero section */}
        <header className="bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
            <div className="space-y-6">
              <div className="space-y-3">
                {category && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
                    {category}
                  </span>
                )}
                <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 leading-tight">
                  {title}
                </h1>
              </div>

              {excerpt && (
                <p className="text-lg md:text-xl text-gray-600 max-w-3xl">
                  {excerpt}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">
                    {author || "Justin Young"}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-gray-300" />
                  {date && <span>{date}</span>}
                  {readTime && (
                    <>
                      <span className="w-1 h-1 rounded-full bg-gray-300" />
                      <span>{readTime}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {image && (
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
              <div className="relative rounded-2xl overflow-hidden shadow-xl bg-gray-100">
                <img
                  src={image}
                  alt={title}
                  className="w-full h-72 md:h-96 object-cover"
                />
              </div>
            </div>
          )}
        </header>

        {/* Article body */}
        <main className="pb-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <article className="bg-white rounded-2xl shadow-lg px-6 sm:px-10 py-10 sm:py-12">
              <div className="border-l-4 border-blue-500 pl-4 mb-8 text-sm text-gray-600">
                This guide is for informational purposes only and does not
                replace medical advice. Always consult a healthcare professional
                for personalized treatment.
              </div>

              <div className="text-gray-800 leading-relaxed text-base md:text-lg whitespace-pre-wrap">
                {formatBlogText(text)}
              </div>
            </article>

            {/* Footer author box */}
            <section className="mt-10">
              <div className="bg-blue-50 border border-blue-100 rounded-2xl px-6 py-6 sm:px-8 sm:py-7 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wide mb-1">
                    About the author
                  </h3>
                  <p className="text-base font-semibold text-gray-900">
                    {author || "Justin Young"}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Justin writes practical, experience-driven content to help
                    people manage GERD and related digestive issues with simple,
                    science-backed strategies that fit real life.
                  </p>
                </div>
                <Link
                  to="/blog"
                  className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
                >
                  More articles →
                </Link>
              </div>
            </section>
          </div>
        </main>
      </div>
    </>
  );
}
